/**
 * Mapas de renderizado y helpers compartidos para órdenes de trabajo
 *
 * Usado por OrdenesTrabajo.js y MisOrdenesTrabajo.js para evitar duplicación.
 */

import React from 'react';
import HighlightedText from '../components/common/HighlightedText';
import { renderEquipoCell } from './renderHelpers';
import { isOrdenTrabajoVencida, getDiasHastaOrdenTrabajo } from './dateHelpers';

export const dotColors = {
  programado: 'var(--info-color)',
  en_progreso: 'var(--warning-color)',
  completado: 'var(--success-color)',
  cancelado: 'var(--text-secondary)'
};

export const prioridadBadge = {
  critica: 'badge-danger',
  alta: 'badge-danger',
  media: 'badge-warning',
  baja: 'badge-success'
};

export const prioridadBorder = {
  critica: 'ot-card-danger',
  alta: 'ot-card-danger',
  media: 'ot-card-warning',
  baja: 'ot-card-success'
};

// ============================================================
// Helpers compartidos
// ============================================================

/**
 * Formatea estado a texto legible (ej: "en_progreso" → "En progreso")
 *
 * @param {string} estado
 * @returns {string}
 */
export function formatEstado(estado) {
  if (!estado) return '-';
  return estado.replace('_', ' ').replace(/^\w/, c => c.toUpperCase());
}

// ============================================================
// Columnas compartidas
// ============================================================

/**
 * Genera las columnas base de la tabla de órdenes de trabajo
 *
 * @param {Object} [options]
 * @param {boolean} [options.includeAsignado=false] - Incluir columna "Asignado"
 * @returns {Array<Object>} Array de configuración de columnas para EntityTable
 */
export function getOTBaseColumns({ includeAsignado = false } = {}) {
  const columns = [
    {
      key: 'codigo_ot',
      label: 'Código OT',
      sortable: true,
      title: true,
      render: (row, searchTerm) => (
        <HighlightedText text={row.codigo_ot || ''} searchTerm={searchTerm} />
      )
    },
    {
      key: 'fecha_programada',
      label: 'F. Programada',
      sortable: true,
      render: (row) => row.fecha_programada
        ? new Date(row.fecha_programada).toLocaleDateString('es-CL')
        : '-'
    },
    {
      key: 'fecha_ejecucion',
      label: 'F. Ejecución',
      sortable: true,
      render: (row) => {
        const vencido = isOrdenTrabajoVencida(row.fecha_ejecucion, row.estado);
        const dias = getDiasHastaOrdenTrabajo(row.fecha_ejecucion);

        return (
          <div className="fecha-column-container">
            <span>
              {row.fecha_ejecucion
                ? new Date(row.fecha_ejecucion).toLocaleDateString('es-CL')
                : '-'
              }
            </span>
            {vencido && dias !== null && (
              <div className="badges-row">
                <span className="badge badge-danger badge-vencido">
                  <i className="fas fa-clock"></i> {Math.abs(dias)} días
                </span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'desfase',
      label: 'Desfase',
      render: (row) => {
        if (!row.fecha_programada || !row.fecha_ejecucion) return '-';
        const prog = new Date(row.fecha_programada);
        const ejec = new Date(row.fecha_ejecucion);
        prog.setHours(0, 0, 0, 0);
        ejec.setHours(0, 0, 0, 0);
        const dias = Math.round((ejec - prog) / (1000 * 60 * 60 * 24));
        if (dias === 0) return <span className="text-muted">0</span>;
        const badge = dias > 0 ? 'badge-warning' : 'badge-success';
        return <span className={`badge ${badge}`}>{dias > 0 ? '+' : ''}{dias}d</span>;
      }
    },
    {
      key: 'equipo',
      label: 'Equipo',
      render: (row, searchTerm) => renderEquipoCell(row.equipo, searchTerm)
    },
    {
      key: 'plantilla',
      label: 'Plantilla',
      render: (row, searchTerm) =>
        row.plantilla?.nombre
          ? <HighlightedText text={row.plantilla.nombre} searchTerm={searchTerm} />
          : <span className="text-muted">Sin plantilla</span>
    },
    {
      key: 'prioridad',
      label: 'Prioridad',
      render: (row, searchTerm) => (
        <span className={`badge ${prioridadBadge[row.prioridad]}`}>
          <HighlightedText text={row.prioridad || ''} searchTerm={searchTerm} />
        </span>
      )
    },
    {
      key: 'estado',
      label: 'Estado',
      render: (row, searchTerm) => (
        <span className="status-dot-cell">
          <span className="status-dot" style={{ backgroundColor: dotColors[row.estado] }} />
          <HighlightedText text={row.estado?.replace('_', ' ') || ''} searchTerm={searchTerm} />
        </span>
      )
    }
  ];

  if (includeAsignado) {
    columns.push({
      key: 'asignado',
      label: 'Asignado',
      render: (row, searchTerm) =>
        row.usuario_asignado?.nombre
          ? <HighlightedText text={row.usuario_asignado.nombre} searchTerm={searchTerm} />
          : <span className="text-muted">Sin asignar</span>
    });
  }

  columns.push({ key: 'acciones', label: 'Acciones', align: 'center' });

  return columns;
}

// ============================================================
// Handlers compartidos (factories)
// ============================================================

/**
 * Crea los handlers de historial e informes compartidos entre ambas páginas
 *
 * IMPORTANTE: Llamar directamente en el cuerpo del componente, sin envolver en
 * useMemo/useCallback. El getter `getInformeItem` captura estado via closure y
 * necesita recrearse cada render para leer el valor actual. Si se requiere
 * memoización en el futuro, reemplazar el getter por un useRef (ver patrón en
 * ActividadesPlantillaList.js:158-160).
 *
 * @param {Object} deps
 * @param {Object} deps.apiService - Servicio API
 * @param {Function} deps.showToast - Función de notificación
 * @param {Function} deps.downloadFile - Función para descargar archivos
 * @param {Function} deps.setHistorialDialog - Setter del estado del modal de historial
 * @param {Function} deps.setInformeDialog - Setter del estado del modal de informe
 * @param {Function} deps.setGenerandoInforme - Setter del estado de loading del informe
 * @param {Function} deps.getInformeItem - Getter del item actual del modal de informe (closure, NO memoizar)
 * @param {Function} [deps.onInformeSuccess] - Callback tras generar informe (ej: refetch)
 * @returns {{ handleVerHistorial: Function, handleGenerarInforme: Function, handleConfirmarInforme: Function }}
 */
export function createOTInformeHandlers({
  apiService,
  showToast,
  downloadFile,
  setHistorialDialog,
  setInformeDialog,
  setGenerandoInforme,
  getInformeItem,
  onInformeSuccess
}) {
  const handleVerHistorial = async (row) => {
    try {
      const response = await apiService.getHistorialProgramacion(row.id);
      if (response.success) {
        setHistorialDialog({ open: true, data: response.data, item: row });
      }
    } catch {
      showToast('Error al cargar historial', 'error');
    }
  };

  const handleGenerarInforme = async (row) => {
    if (row.informe_id) {
      try {
        const { blob, filename } = await apiService.descargarInformeOrdenTrabajo(row.id);
        downloadFile(blob, filename);
      } catch {
        showToast('Error al descargar el informe', 'error');
      }
      return;
    }
    setInformeDialog({ open: true, item: row });
  };

  const handleConfirmarInforme = async (observaciones) => {
    const item = getInformeItem();
    if (!item) return;
    setGenerandoInforme(true);
    try {
      await apiService.generarInformeOrdenTrabajo(item.id, { observaciones });
      const { blob, filename } = await apiService.descargarInformeOrdenTrabajo(item.id);
      downloadFile(blob, filename);
      setInformeDialog({ open: false, item: null });
      showToast('Informe generado y firmado exitosamente', 'success');
      if (onInformeSuccess) onInformeSuccess();
    } catch {
      showToast('Error al generar el informe', 'error');
    } finally {
      setGenerandoInforme(false);
    }
  };

  return { handleVerHistorial, handleGenerarInforme, handleConfirmarInforme };
}
